import { Button, Col, Form, Input, InputNumber, Modal, Row, Select, Switch } from "antd";
import TextArea from "antd/lib/input/TextArea";
import CustomDatePicker from "common/CustomDatePicker";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import {
  BUYER_INQUIRY_GET_COUNTRIES_ENDPOINT,
  BUYER_INQUIRY_MULTI_STYLE_ADD_ENDPOINT,
  GARMENT_CATEGORY_LIST_ENDPOINT,
  GARMENT_FOR_LIST_ENDPOINT,
  GARMENT_TYPE_LIST_ENDPOINT,
} from "../../../apiServices/API_ENDPOINTS";
import { getData, postData } from "../../../apiServices/common";
import HandleNotification from "../../../common/Notification";
import {
  calculateExpectedOrderQuantity,
  commaSeparateNumber,
  isArrayAndHasValue,
  roundExpectedOrderQty,
} from "../../../utils/functions";
import { FABRIC_TYPE } from "../helper";

const { Option } = Select;

const AddMultiStyleModal = ({ open, inquiry, onCancel, onSuccess }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [garmentCategoryList, setGarmentCategoryList] = useState([]);
  const [garmentTypeList, setGarmentTypeList] = useState([]);
  const [garmentForList, setGarmentForList] = useState([]);
  const [countryList, setCountryList] = useState([]);

  const getAllGarmentCategory = useCallback(async (filterValues) => {
    const bodyData = {
      per_page: filterValues?.per_page || 20,
      ...filterValues,
    };
    const response = await getData(GARMENT_CATEGORY_LIST_ENDPOINT, false, bodyData);
    if (response?.data?.code === 200) {
      setGarmentCategoryList(response?.data?.data?.data || []);
    }
  }, []);

  const getAllGarmentType = useCallback(async (filterValues) => {
    const bodyData = {
      per_page: filterValues?.per_page || 20,
      ...filterValues,
    };
    const response = await getData(GARMENT_TYPE_LIST_ENDPOINT, false, bodyData);
    if (response?.data?.code === 200) {
      setGarmentTypeList(response?.data?.data?.data || []);
    }
  }, []);

  const getAllGarmentFor = useCallback(async (filterValues) => {
    const bodyData = {
      per_page: filterValues?.per_page || 20,
      ...filterValues,
    };
    const response = await getData(GARMENT_FOR_LIST_ENDPOINT, false, bodyData);
    if (response?.data?.code === 200) {
      setGarmentForList(response?.data?.data?.data || []);
    }
  }, []);

  const getCountries = useCallback(async (filterValues) => {
    const bodyData = {
      per_page: 500,
      status: "active",
      ...(filterValues?.name && { name: filterValues.name }),
    };
    const response = await getData(BUYER_INQUIRY_GET_COUNTRIES_ENDPOINT, false, bodyData);
    if (response?.data?.code === 200) {
      setCountryList(response?.data?.data?.data || []);
    }
  }, []);

  useEffect(() => {
    if (!open) return;

    form.resetFields();
    form.setFieldsValue({
      washing: false,
      snap_button: false,
      extra_percent: 0,
    });

    getAllGarmentCategory();
    getAllGarmentType();
    getAllGarmentFor();
    getCountries();
  }, [
    open,
    form,
    getAllGarmentCategory,
    getAllGarmentType,
    getAllGarmentFor,
    getCountries,
  ]);

  const recalculateExpectedQty = () => {
    setTimeout(() => {
      const orderQty = form.getFieldValue("order_quantity");
      const percentage = form.getFieldValue("extra_percent") || 0;
      form.setFieldsValue({
        expected_order_quantity: calculateExpectedOrderQuantity(orderQty, percentage),
      });
    }, 0);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (!inquiry?.id) return;

      setLoading(true);

      const orderQuantity = values?.order_quantity != null ? Number(values?.order_quantity) : null;
      const extraPercent = values?.extra_percent != null ? Number(values?.extra_percent) : 0;
      const expectedOrderQuantity =
        values?.expected_order_quantity != null
          ? roundExpectedOrderQty(Number(values?.expected_order_quantity))
          : roundExpectedOrderQty(calculateExpectedOrderQuantity(orderQuantity, extraPercent));

      const payload = {
        style_no: values?.style_no,
        garment_category_id: values?.garment_category_id,
        garment_type_id: values?.garment_type_id,
        garment_for_id: values?.garment_for_id,
        order_quantity: orderQuantity,
        extra_percent: extraPercent,
        expected_order_quantity: expectedOrderQuantity,
        expected_delivery_date: values?.expected_delivery_date
          ? moment(values?.expected_delivery_date).format("YYYY-MM-DD")
          : null,
        production_month_from: values?.production_month_from
          ? moment(values?.production_month_from).format("YYYY-MM-DD")
          : null,
        production_month_to: values?.production_month_to
          ? moment(values?.production_month_to).format("YYYY-MM-DD")
          : null,
        description: values?.description,
        season: values?.season,
        size_range: values?.size_range,
        fabric_content: values?.fabric_content,
        fob: values?.fob != null ? Number(values?.fob) : null,
        country_id: values?.country_id ? Number(values?.country_id) : null,
        washing: values?.washing ? "Yes" : "No",
        snap_button: values?.snap_button ? "Yes" : "No",
        // Required by backend validateMultiStyle; prefer parent inquiry value
        fabric_status: inquiry?.fabric_status || "N/A",
        buyer_id: inquiry?.buyer_id || inquiry?.buyer_info?.id || null,
        Isbuying_office_id: inquiry?.buying_office_id || inquiry?.buying_office_info?.id || null,
        sub_contract_factory_id:
          inquiry?.sub_contract_factory_id || inquiry?.factory_info?.id || null,
      };

      const response = await postData(
        `${BUYER_INQUIRY_MULTI_STYLE_ADD_ENDPOINT}/${inquiry.id}`,
        payload,
      );

      if (response?.code === 200) {
        HandleNotification(
          "success",
          "Success!",
          Array.isArray(response?.message)
            ? response.message[0]
            : response?.message || "Multi style added successfully",
        );
        form.resetFields();
        onSuccess?.();
        onCancel?.();
      }
      // On fail: postData already shows the API error toast; keep modal open
    } catch (error) {
      // Form validation errors — keep modal open, no extra toast
      if (error?.errorFields) return;
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      title={`Add Multi Style${inquiry?.inquiry_no ? ` - ${inquiry.inquiry_no}` : ""}`}
      onCancel={onCancel}
      width={1100}
      destroyOnClose
      maskStyle={{ backdropFilter: "blur(4px)" }}
      footer={
        <>
          <Button onClick={onCancel}>Cancel</Button>
          <Button type="primary" loading={loading} onClick={handleSubmit}>
            Add Multi Style
          </Button>
        </>
      }
    >
      <Form form={form} layout="vertical" size="small">
        <Row gutter={12}>
          <Col span={6}>
            <Form.Item
              label="Style No."
              name="style_no"
              rules={[{ required: true, message: "Style No. is required" }]}
            >
              <Input placeholder="Style No." />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label="Garments Category"
              name="garment_category_id"
              rules={[{ required: true, message: "Garments Category is required" }]}
            >
              <Select
                showSearch
                placeholder="Select a category"
                optionFilterProp="children"
                allowClear
                onSearch={(value) => getAllGarmentCategory({ name: value })}
              >
                {isArrayAndHasValue(garmentCategoryList) &&
                  garmentCategoryList.map((item) => (
                    <Option value={item?.id} key={item?.id}>
                      {item?.name || ""}
                    </Option>
                  ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label="Garments Type"
              name="garment_type_id"
              rules={[{ required: true, message: "Garments Type is required" }]}
            >
              <Select
                showSearch
                placeholder="Select a type"
                optionFilterProp="children"
                allowClear
                onSearch={(value) => getAllGarmentType({ name: value })}
              >
                {isArrayAndHasValue(garmentTypeList) &&
                  garmentTypeList.map((item) => (
                    <Option value={item?.id} key={item?.id}>
                      {item?.name || ""}
                    </Option>
                  ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label="Garments For"
              name="garment_for_id"
              rules={[{ required: true, message: "Garments For is required" }]}
            >
              <Select
                showSearch
                placeholder="Select for"
                optionFilterProp="children"
                allowClear
                onSearch={(value) => getAllGarmentFor({ name: value })}
              >
                {isArrayAndHasValue(garmentForList) &&
                  garmentForList.map((item) => (
                    <Option value={item?.id} key={item?.id}>
                      {item?.name || ""}
                    </Option>
                  ))}
              </Select>
            </Form.Item>
          </Col>

          <Col span={6}>
            <Form.Item
              label="Order Qty"
              name="order_quantity"
              rules={[{ required: true, message: "Order Qty is required" }]}
            >
              <InputNumber
                style={{ width: "100%" }}
                placeholder="Order Qty"
                formatter={(value) => commaSeparateNumber(value)}
                onChange={recalculateExpectedQty}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label="Cutting (%)"
              name="extra_percent"
              rules={[
                { required: true, message: "Cutting(%) is required" },
                {
                  validator: (_, value) => {
                    if (value > 100) {
                      return Promise.reject(new Error("Cutting(%) cannot be greater than 100"));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <InputNumber
                style={{ width: "100%" }}
                placeholder="Cutting (%)"
                min={0}
                max={100}
                onChange={recalculateExpectedQty}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="Exp. Order Qty. (Calculated)" name="expected_order_quantity">
              <InputNumber
                style={{ width: "100%" }}
                placeholder="Calculated Qty."
                disabled
                precision={0}
                formatter={(value) => commaSeparateNumber(value)}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label="Exp. Delivery Date"
              name="expected_delivery_date"
              rules={[{ required: true, message: "Exp. Delivery Date is required" }]}
            >
              <CustomDatePicker align="right" size="small" />
            </Form.Item>
          </Col>

          <Col span={6}>
            <Form.Item
              label="Production Month From"
              name="production_month_from"
              rules={[{ required: true, message: "Production Month From is required" }]}
            >
              <CustomDatePicker align="right" size="small" />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label="Production Month To"
              name="production_month_to"
              rules={[{ required: true, message: "Production Month To is required" }]}
            >
              <CustomDatePicker align="right" size="small" />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label="Description"
              name="description"
              rules={[{ required: true, message: "Description is required" }]}
            >
              <TextArea size="small" placeholder="Description" rows={1} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label="Month"
              name="season"
              normalize={(value) => {
                if (value === "" || value === null || value === undefined) return null;
                const num = Number(value);
                return Number.isNaN(num) ? null : Math.trunc(num);
              }}
              rules={[
                { required: true, message: "Month is required" },
                {
                  validator: (_, value) => {
                    if (value === null || value === undefined || value === "") {
                      return Promise.resolve();
                    }
                    if (Number.isInteger(Number(value))) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error("Month must be an integer"));
                  },
                },
              ]}
            >
              <InputNumber
                style={{ width: "100%" }}
                placeholder="Month"
                precision={0}
                controls={false}
                parser={(value) => {
                  const cleaned = String(value ?? "").replace(/\D/g, "");
                  return cleaned === "" ? "" : Number(cleaned);
                }}
              />
            </Form.Item>
          </Col>

          <Col span={6}>
            <Form.Item
              label="Size Range"
              name="size_range"
              rules={[{ required: true, message: "Size Range is required" }]}
            >
              <Input placeholder="Size Range" />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label="Fabric Type"
              name="fabric_content"
              rules={[{ required: true, message: "Fabric Type is required" }]}
            >
              <Select
                showSearch
                placeholder="Select Fabric Type"
                optionFilterProp="children"
                allowClear
              >
                {isArrayAndHasValue(FABRIC_TYPE) &&
                  FABRIC_TYPE.map((item) => (
                    <Option value={item?.value} key={item?.id}>
                      {item?.label || ""}
                    </Option>
                  ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label="FOB $"
              name="fob"
              rules={[{ required: true, message: "FOB $ is required" }]}
            >
              <InputNumber
                style={{ width: "100%" }}
                placeholder="FOB"
                formatter={(value) => commaSeparateNumber(value)}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label="Country"
              name="country_id"
              rules={[{ required: true, message: "Country is required" }]}
            >
              <Select
                showSearch
                placeholder="Select Country"
                optionFilterProp="children"
                allowClear
                filterOption={false}
                onSearch={(value) => getCountries({ name: value })}
                onClear={() => getCountries()}
              >
                {isArrayAndHasValue(countryList) &&
                  countryList.map((item) => (
                    <Option value={item?.id} key={item?.id}>
                      {item?.name || ""}
                    </Option>
                  ))}
              </Select>
            </Form.Item>
          </Col>

          <Col span={6}>
            <Form.Item
              name="washing"
              label="Washing"
              valuePropName="checked"
              rules={[
                {
                  validator: (_, value) => {
                    if (value === undefined || value === null) {
                      return Promise.reject(new Error("Washing is required"));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="snap_button"
              label="Snap Button"
              valuePropName="checked"
              rules={[
                {
                  validator: (_, value) => {
                    if (value === undefined || value === null) {
                      return Promise.reject(new Error("Snap Button is required"));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default AddMultiStyleModal;
